package components.project.configuration.tabs

import components.project.QuoteConfigurationTabProps
import components.project.configuration.QuotePositionsTable
import components.project.configuration.QuotePositionsTableMode
import components.project.configuration.QuoteSnapshotMode
import components.project.configuration.isConfigurationEditable
import components.project.isProjectEditable
import it.neckar.commons.kotlin.js.LocalStorageKey
import it.neckar.commons.kotlin.js.LocalStorageSupport
import it.neckar.commons.kotlin.js.safeGet
import it.neckar.lizergy.model.financial.quote.query.VisibilityQuery
import it.neckar.lizergy.model.project.process.state.current
import it.neckar.lizergy.model.project.process.state.toNewProcessState
import it.neckar.react.common.*
import it.neckar.react.common.form.*
import it.neckar.react.common.form.EditableStatus.Companion.toEditableStatus
import react.*
import react.dom.*
import router.useConfigurationIdFromUrl
import router.useDocumentTitle
import router.useLoadResolvedProjectFromUrl
import store.hooks.useLoadProcessStates
import store.hooks.useLoadProcessStatesForComponent
import store.hooks.useRequireCompanyForLoggedInUser

/**
 * Visualizes the quote positions in a tab
 */
val QuotePositionsOverviewFromUrl: FC<Props> = fc("QuotePositionsOverviewFromUrl") {
  val resolvedProject = useLoadResolvedProjectFromUrl()
  val configurationId = useConfigurationIdFromUrl()
  val companyName = useRequireCompanyForLoggedInUser().simpleName
  val allProcessStatesForComponents = useLoadProcessStates()

  useDocumentTitle(companyName, "Rechnungsvorschau", allProcessStatesForComponents?.let { resolvedProject?.getDisplayName(it) }, resolvedProject?.get(configurationId)?.configurationName)

  busyIfNull(resolvedProject) { loadedProject ->
    val quoteConfiguration = loadedProject[configurationId]
    val processStatesForConfiguration = useLoadProcessStatesForComponent(quoteConfiguration.uuid)
    val currentProcessStateForConfiguration = processStatesForConfiguration?.current()?.processState ?: quoteConfiguration.processState?.currentValue?.toNewProcessState()
    val editableStatus = loadedProject.isProjectEditable().and(currentProcessStateForConfiguration.isConfigurationEditable())

    QuotePositionsOverview {
      attrs {
        this.project = loadedProject
        this.quoteConfiguration = quoteConfiguration
        this.editableStatus = editableStatus
      }
    }
  }
}

val QuotePositionsOverview: FC<QuoteConfigurationTabProps> = fc("QuotePositionsOverview") { props ->
  val quoteConfiguration = props::quoteConfiguration.safeGet()
  val editableStatus = props::editableStatus.safeGet()

  val currentQuoteSnapshot = quoteConfiguration.currentQuoteSnapshot

  val quotePositionsTableMode = LocalStorageSupport.loadFromLocalStorage(
    key = LocalStorageKey("QuotePositionsTableMode"),
    serializer = QuotePositionsTableMode.serializer(),
  ) ?: QuotePositionsTableMode.Simplified

  val quotePositionsTableModeState = useState(quotePositionsTableMode)
  val quoteSnapshotModeState = useState(if (currentQuoteSnapshot == null || editableStatus == EditableStatus.Editable) QuoteSnapshotMode.UpToDate else QuoteSnapshotMode.Frozen)


  div("row gx-0 my-3") {

    div("col-lg-6") {
      floatingSelectEnum(
        valueAndSetter = quotePositionsTableModeState,
        formatter = {
          when (it) {
            QuotePositionsTableMode.Simplified -> "Vereinfacht"
            QuotePositionsTableMode.Detailed -> "Detailliert"
          }
        },
        availableOptions = QuotePositionsTableMode.entries,
        fieldName = "quotePositionsTableMode",
        title = "Ansichtsmodus",
        additionalOnChange = {
          LocalStorageSupport.saveToLocalStorageOptional(LocalStorageKey("QuotePositionsTableMode"), it, QuotePositionsTableMode.serializer())
        },
      ) {
        attrs {
          mergedRight()
        }
      }
    }
    div("col-lg-6") {
      floatingSelectEnum(
        valueAndSetter = quoteSnapshotModeState,
        formatter = {
          when (it) {
            QuoteSnapshotMode.Frozen -> "Eingefroren"
            QuoteSnapshotMode.UpToDate -> "Aktuell"
          }
        },
        availableOptions = QuoteSnapshotMode.entries,
        fieldName = "quoteSnapshotMode",
        title = "Preise",
        editableStatus = (currentQuoteSnapshot != null).toEditableStatus(),
        additionalOnChange = {
          LocalStorageSupport.saveToLocalStorageOptional(LocalStorageKey("QuoteSnapshotMode"), it, QuoteSnapshotMode.serializer())
        },
      ) {
        attrs {
          mergedLeft()
        }
      }
    }

    div("col-12 mt-4") {
      QuotePositionsTable {
        attrs {
          this.quoteElements = when (quoteSnapshotModeState.value) {
            QuoteSnapshotMode.Frozen -> {
              currentQuoteSnapshot?.quote?.quoteElements ?: throw IllegalStateException("QuoteSnapshotMode.Frozen requires a quote snapshot")
            }

            QuoteSnapshotMode.UpToDate -> quoteConfiguration.calculatedQuoteElements
          }
          this.mode = quotePositionsTableModeState.value
          this.query = when (quotePositionsTableModeState.value) {
            QuotePositionsTableMode.Simplified -> VisibilityQuery.onlyPublicSkipZero
            QuotePositionsTableMode.Detailed -> VisibilityQuery.skipZero
          }
        }
      }
    }

  }

}
